import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import TableWithPagination from "../../../components/TableWithPagination";
import { useLoanContext } from "../LoanContext";
import PrimaryButton from "../../../components/styled/PrimaryButton";
import SecondaryButton from "../../../components/styled/SecondaryButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTransactionSummaryValidation } from "../../../hooks/useValidation";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { formatDateToDDMMYYYY, formatDateForAPI } from "../../../utils/dateUtils";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';

const FooterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

interface Transaction {
  transactionTypeId: string;
  transactionTypeName: string;
  transactionDate: Date | string;
  transactionAmount: number;
  balance: number;
  interestRate: number;
  lastTransactionType?: string;
}

type TxnFilter = "all" | "payment" | "non-payment";
type DateFilter = "1month" | "3months" | "6months" | "1year" | "custom";

const parseDateSafe = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;

  const str = String(v).trim();
  if (!str) return null;

  // First, try native parsing (covers ISO 8601 and timestamp strings)
  const iso = new Date(str);
  if (!isNaN(iso.getTime())) return iso;

  // Fallback: DD/MM/YYYY
  const ddmmyyyy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    const year = y.length === 2 ? Number(`20${y}`) : Number(y);
    const parsed = new Date(year, Number(m) - 1, Number(d));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const atMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const normalizeTxnName = (value?: string | null) =>
  value ? value.toString().toLowerCase().replace(/[^a-z0-9]/g, "") : "";

const TransactionSummaryTab: React.FC = () => {
  const { loan } = useLoanContext();
  const loanId = useParams().loanId;

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [transactionType, setTransactionType] = useState<TxnFilter>("all");
  const [dateFilterType, setDateFilterType] = useState<DateFilter>("1year");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [pendingDateFilterType, setPendingDateFilterType] = useState<DateFilter>("1year");
  const [pendingFromDate, setPendingFromDate] = useState<Date | null>(null);
  const [pendingToDate, setPendingToDate] = useState<Date | null>(null);

  const [bannerError, setBannerError] = useState<string>("");

  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState<"PDF" | "Excel">("PDF");

  const { validateTransactionSummaryForm } = useTransactionSummaryValidation();

  useEffect(() => {
    if (!loanId || !loan) {
      setError(!loanId ? "No loanId provided." : "Loading loan data...");
      setLoading(false);
      return;
    }
    const fetchTransactions = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`/api/transactions?loanId=${loanId}`, { withCredentials: true });
        const data = Array.isArray(response.data) ? response.data : [];
        setTransactions(data);
        setFilteredTransactions(data);
      } catch (err: unknown) {
        setError(String((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Failed to fetch transactions."));
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [loanId, loan]);

  const paymentTransactionNames = useMemo(
    () => [
      "Additional Repayment",
      "Adjusted Interest posting",
      "Cancelled Payment",
      "Charges",
      "Deposit Payment",
      "Disbursal reversal",
      "Excess Payment",
      "Payment Reversal",
      "Principal Adjustment- Add",
      "Principal Adjustment- Sub",
      "Principal Adjustment -Redraw",
      "Rebate Payment transaction",
      "Refinance payment",
      "Refund",
      "Refund reversal",
      "Regular disbursal",
      "Regular Interest posting",
      "Regular Payment",
      "Waived",
      "Write off recovery",
    ],
    []
  );

  const nonPaymentTransactionNames = useMemo(
    () => [
      "Additional Interest",
      "Additional Interest- Excess Payoff",
      "Additional Interest Rate Change",
      "Additional Interest Rate Change reversal",
      "Adjusted Interest",
      "Adjusted Interest Excess Payoff",
      "Adjustment Entry Payment Reversal",
      "Bill/Due date",
      "Charge off",
      "Charge off Reversal",
      "Closure Tolerance",
      "Contingency Status change",
      "Credit Limit Change",
      "Delinquent Pmt Plan",
      "Delinquent Pmt Plan reversal",
      "Deposit Adjustment-Add",
      "Deposit Adjustment-Subtract",
      "Deposit Adjustment Transaction",
      "Deposit Transfer-Add",
      "Deposit Transfer-Subtract",
      "Due date change",
      "Due Day Change",
      "Extension",
      "Extension Reversal",
      "Index Rate Change",
      "Index Rate Change reversal",
      "Interest Adjustment",
      "Interest Posting- Excess Payoff",
      "Interest Posting IOA on IOA",
      "IOA Bills",
      "IOA on unpaid Principal /Interest",
      "Loan Facility Amount Increase",
      "Loan Facility Amount Decrease",
      "Manual Adjusted Interest Posting",
      "Manual Loan closure",
      "Manual Loan closure reversal",
      "Paid Charge Unwaive",
      "Paid Charge Waiver",
      "Payment Change",
      "Payment Change reversal",
      "Payment tolerance change",
      "Payoff quote generation",
      "Prepayment Bill",
      "Rate change",
      "Rate change reversal",
      "Redraw balance reduction",
      "Redraw balance reduction reversal",
      "Reschedule",
      "Reschedule reversal",
      "Settled",
      "Sold",
    ],
    []
  );

  const paymentNameSet = useMemo(
    () => new Set(paymentTransactionNames.map(normalizeTxnName)),
    [paymentTransactionNames]
  );
  const nonPaymentNameSet = useMemo(
    () => new Set(nonPaymentTransactionNames.map(normalizeTxnName)),
    [nonPaymentTransactionNames]
  );

  // Explicit aliases for known variants not in the exact allowlists
  const aliasMap = useMemo<Record<string, TxnFilter>>(
    () => ({
      // Add variants here as they appear in data
      regularpaymenttransaction: "payment",
    }),
    []
  );

  const categorizeTransaction = useCallback(
    (txn: Transaction): TxnFilter | "unknown" => {
      const primary = normalizeTxnName(txn.transactionTypeName as string);
      const secondary = normalizeTxnName(txn.lastTransactionType as string);

      const exactMatch = (value: string) => {
        if (!value) return null;
        if (aliasMap[value]) return aliasMap[value];
        if (paymentNameSet.has(value)) return "payment";
        if (nonPaymentNameSet.has(value)) return "non-payment";
        return null;
      };

      return (
        exactMatch(primary) ||
        exactMatch(secondary) ||
        "unknown"
      );
    },
    [paymentNameSet, nonPaymentNameSet, aliasMap]
  );

  useEffect(() => {
    let filtered = [...transactions];
    if (transactionType !== "all") {
      filtered = filtered.filter(
        (txn) => categorizeTransaction(txn) === transactionType
      );
    }

    if (dateFilterType === "custom" && fromDate && toDate) {
      const start = atMidnight(fromDate);
      const end = atMidnight(toDate);
      filtered = filtered.filter((txn) => {
        const d = parseDateSafe(txn.transactionDate);
        return !!d && atMidnight(d) >= start && atMidnight(d) <= end;
      });
    } else if (dateFilterType !== "custom") {
      const now = new Date();
      const cutoff = new Date(now);
      switch (dateFilterType) {
        case "1month":
          cutoff.setMonth(now.getMonth() - 1);
          break;
        case "3months":
          cutoff.setMonth(now.getMonth() - 3);
          break;
        case "6months":
          cutoff.setMonth(now.getMonth() - 6);
          break;
        case "1year":
          cutoff.setFullYear(now.getFullYear() - 1);
          break;
      }
      const cutoffMid = atMidnight(cutoff);
      filtered = filtered.filter((txn) => {
        const d = parseDateSafe(txn.transactionDate);
        return !!d && atMidnight(d) >= cutoffMid;
      });
    }
    setFilteredTransactions(filtered);
  }, [
    transactions,
    transactionType,
    dateFilterType,
    fromDate,
    toDate,
    paymentTransactionNames,
    nonPaymentTransactionNames,
    categorizeTransaction,
  ]);

  // Filters dialog open handling
  const openFilterDialog = () => {
    setPendingDateFilterType(dateFilterType);
    setPendingFromDate(fromDate);
    setPendingToDate(toDate);
    setBannerError("");
    setFilterDialogOpen(true);
  };

  useEffect(() => {
    if (pendingDateFilterType !== "custom") {
      setPendingFromDate(null);
      setPendingToDate(null);
      setBannerError("");
    }
  }, [pendingDateFilterType]);

  const handleApplyFilters = () => {
    let err = "";

    if (pendingDateFilterType === "custom") {
      if (!pendingFromDate || !pendingToDate) {
        err = "Please provide both From and To dates.";
      } else {
        const start = atMidnight(pendingFromDate);
        const end = atMidnight(pendingToDate);
        const tenYearsAgo = new Date();
        tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);

        if (end < start) {
          err = "To date must be after From date.";
        } else if (start < atMidnight(tenYearsAgo)) {
          err = "From date must be within the last 10 years.";
        } else if (end > atMidnight(new Date())) {
          err = "To date cannot be in the future.";
        }
      }
    }

    if (!err) {
      const hookErrors = validateTransactionSummaryForm(
        pendingFromDate ? formatDateForAPI(pendingFromDate) : "",
        pendingToDate ? formatDateForAPI(pendingToDate) : "",
        "",
        false
      );
      if (hookErrors.period) {
        err = hookErrors.period;
      }
    }

    if (err) {
      setBannerError(err);
      return;
    }

    setDateFilterType(pendingDateFilterType);
    setFromDate(pendingFromDate);
    setToDate(pendingToDate);
    setBannerError("");
    setFilterDialogOpen(false);
  };



  const handleDownload = async (format: "PDF" | "Excel") => {
    const data = filteredTransactions.map((txn) => ({
      Date: txn.transactionDate ? formatDateToDDMMYYYY(txn.transactionDate as string) : "",
      Description: txn.transactionTypeName || txn.lastTransactionType || "N/A",
      "Interest Rate": txn.interestRate !== undefined && txn.interestRate !== null ? `${txn.interestRate.toFixed(2)} %` : "",
      Amount: txn.transactionAmount !== undefined && txn.transactionAmount !== null ? formatAmount(txn.transactionAmount) : "",
      "Closing Balance": txn.balance !== undefined && txn.balance !== null ? formatAmount(txn.balance) : "",
    }));
  
    const headers = ["Date", "Description", "Interest Rate", "Amount", "Closing Balance"];
  
    if (format === "Excel") {
      try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Transactions");

        // Define columns so object keys map correctly and widths adjust later
        worksheet.columns = headers.map(h => ({ header: h, key: h, width: 15 }));

        // Add data rows using object mapping (preserves order via columns definition)
        data.forEach(d => worksheet.addRow(d));

        // Auto-fit widths (basic heuristic)
        worksheet.columns.forEach(col => {
          const rawValues = Array.isArray(col.values) ? col.values : [];
          const cellStrings = rawValues.slice(2).map(v => (v == null ? "" : String(v))); // skip placeholder indexes
          const values = [String(col.header ?? "")].concat(cellStrings);
          const maxLen = Math.min(40, Math.max(12, ...values.map(v => v.length)));
          col.width = maxLen + 2;
        });

        // Bold header row styling
        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true };

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `transaction_summary_${loanId}_${new Date().toISOString().slice(0,10)}.xlsx`);
      } catch {
        setBannerError("Failed to generate Excel file.");
      }
      return;
    } else {
      const doc = new jsPDF();
      autoTable(doc, {
        head: [headers],
        body: data.map(row => headers.map(h => String(row[h as keyof typeof row] ?? "")))
      });
      doc.save(`transaction_summary_${loanId}_${new Date().toISOString().slice(0, 10)}.pdf`);
    }
  };
  

  if (loading)
    return (
      <Box textAlign="center" mt={8}>
        <Typography>Loading transactions...</Typography>
      </Box>
    );
  if (error)
    return (
      <Box textAlign="center" mt={8}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: "100%", maxWidth: "1280px", mx: "auto", px: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <FormControl component="fieldset">
            <FormLabel component="legend">Transaction Type</FormLabel>
            <RadioGroup
              row
              value={transactionType}
              onChange={(e) =>
                setTransactionType(e.target.value as TxnFilter)
              }
              sx={{ gap: "32px" }}
            >
              <FormControlLabel
                value="payment"
                control={<Radio />}
                label="Payment"
              />
              <FormControlLabel
                value="non-payment"
                control={<Radio />}
                label="Non-Payment"
              />
              <FormControlLabel value="all" control={<Radio />} label="All" />
            </RadioGroup>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={openFilterDialog}
            sx={{ height: "fit-content" }}
          >
            Filter
          </Button>
        </Box>

        {(transactionType !== "all" || dateFilterType !== "1year") && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Active Filters:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {transactionType !== "all" && (
                <Chip
                  label={
                    transactionType === "payment"
                      ? "Payment Transactions"
                      : "Non-Payment Transactions"
                  }
                  size="small"
                  onDelete={() => setTransactionType("all")}
                  sx={{
                    background: "#3C184E",
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 2,
                    height: "30px",
                    color: "#fff",
                  }}
                />
              )}
              {dateFilterType !== "1year" && (
                <Chip
                  label={
                    dateFilterType === "custom" && fromDate && toDate
                      ? `Date: ${formatDateToDDMMYYYY(fromDate)} - ${formatDateToDDMMYYYY(
                          toDate
                        )}`
                      : `Date: ${dateFilterType}`
                  }
                  size="small"
                  onDelete={() => {
                    setDateFilterType("1year");
                    setFromDate(null);
                    setToDate(null);
                  }}
                  sx={{
                    background: "#3C184E",
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 2,
                    height: "30px",
                    color: "#fff",
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ width: "100%", overflowX: "auto", borderRadius: 2 }}>
          <TableWithPagination
            columns={[
              {
                label: "Date",
                key: "transactionDate",
                sortable: true,
                sortValue: (row: Record<string, unknown>) => {
                  const parsed = parseDateSafe(row.transactionDate);
                  return parsed ? parsed.getTime() : null;
                },
                renderCell: (row: Record<string, unknown>) =>
                  row.transactionDate
                    ? formatDateToDDMMYYYY(row.transactionDate as string)
                    : "",
              },
              {
                label: "Amount",
                key: "transactionAmount",
                renderCell: (row: Record<string, unknown>) => {
                  const value = row.transactionAmount;
                  if (value === undefined || value === null || value === "")
                    return "—";
                  const numValue = parseFloat(String(value));
                  return isNaN(numValue) ? "—" : formatAmount(numValue);
                },
              },
              {
                label: "Closing Balance",
                key: "balance",
                renderCell: (row: Record<string, unknown>) => {
                  const value = row.balance;
                  if (value === undefined || value === null || value === "")
                    return "—";
                  const numValue = parseFloat(String(value));
                  return isNaN(numValue) ? "—" : formatAmount(numValue);
                },
              },
              {
                label: "Interest Rate",
                key: "interestRate",
                renderCell: (row: Record<string, unknown>) => {
                  const value = row.interestRate;
                  if (value === undefined || value === null || value === "")
                    return "—";
                  const numValue = parseFloat(String(value));
                  return isNaN(numValue) ? "—" : `${numValue.toFixed(2)}%`;
                },
              },
              {
                label: "Description",
                key: "transactionTypeName",
                renderCell: (row: Record<string, unknown>): React.ReactNode => {
                  return (
                    (row.transactionTypeName as string) ||
                    (row.lastTransactionType as string) ||
                    "N/A"
                  );
                },
              },
            ]}
            rows={filteredTransactions as unknown as Record<string, unknown>[]}
            getRowKey={(row, index) =>
              `${row.transactionTypeId || "unknown"}-${row.transactionDate || "na"}-${
                row.transactionAmount ?? "na"
              }-${row.balance ?? "na"}-${index ?? 0}`
            }
            rowsPerPage={10}
          />
        </Box>

        {/* Filter Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Filter Transactions</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">Date Range</FormLabel>
                <RadioGroup
                  value={pendingDateFilterType}
                  onChange={(e) =>
                    setPendingDateFilterType(
                      e.target.value as
                        | "1month"
                        | "3months"
                        | "6months"
                        | "1year"
                        | "custom"
                    )
                  }
                >
                  <FormControlLabel
                    value="1month"
                    control={<Radio />}
                    label="Last 1 Month"
                  />
                  <FormControlLabel
                    value="3months"
                    control={<Radio />}
                    label="Last 3 Months"
                  />
                  <FormControlLabel
                    value="6months"
                    control={<Radio />}
                    label="Last 6 Months"
                  />
                  <FormControlLabel
                    value="1year"
                    control={<Radio />}
                    label="Last 1 Year"
                  />
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label="Custom Range"
                  />
                </RadioGroup>
              </FormControl>

              {pendingDateFilterType === "custom" && (
                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <DatePicker
                    label="From Date"
                    value={pendingFromDate}
                    onChange={(date) => {
                      setPendingFromDate(date);
                      if (bannerError) setBannerError("");
                    }}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label="To Date"
                    value={pendingToDate}
                    onChange={(date) => {
                      setPendingToDate(date);
                      if (bannerError) setBannerError("");
                    }}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>
              )}

              {bannerError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {bannerError}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={() => setFilterDialogOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleApplyFilters} variant="contained">
              Apply
            </PrimaryButton>
          </DialogActions>
        </Dialog>

        {/* Download Confirmation Dialog */}
        <Dialog
          open={downloadDialogOpen}
          onClose={() => setDownloadDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "20px",
              p: 3,
              minWidth: 500,
              maxWidth: 600,
              background: "#18181b",
              position: "relative",
            },
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => {
              setDownloadDialogOpen(false);
              setBannerError("");
            }}
            sx={{ position: "absolute", right: 12, top: 12, color: "#fff", zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>

          <DialogTitle sx={{ p: 0, mb: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Libre Franklin', sans-serif", color: "#fff" }}>
              Download
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0, mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: "'Libre Franklin', sans-serif", mb: 2, color: "#fff" }}>
              Please choose the preferred format to download the selected data.
            </Typography>
            <RadioGroup
              value={pendingDownloadFormat}
              onChange={(e) => setPendingDownloadFormat(e.target.value as "PDF" | "Excel")}
            >
              <FormControlLabel
                value="PDF"
                control={<Radio sx={{ color: "#98773b", "&.Mui-checked": { color: "#98773b" } }} />}
                label="PDF"
                sx={{ "& .MuiFormControlLabel-label": { color: "#fff" } }}
              />
              <FormControlLabel
                value="Excel"
                control={<Radio sx={{ color: "#98773b", "&.Mui-checked": { color: "#98773b" } }} />}
                label="Excel"
                sx={{ "& .MuiFormControlLabel-label": { color: "#fff" } }}
              />
            </RadioGroup>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "flex-end", gap: 2, p: 0 }}>
            <SecondaryButton
              onClick={() => {
                setDownloadDialogOpen(false);
                setBannerError("");
              }}
              sx={{
                border: "1px solid #fff",
                color: "#fff",
                background: "#111",
                borderRadius: "8px",
                px: 4,
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              variant="contained"
              onClick={() => {
                handleDownload(pendingDownloadFormat);
              }}
            >
              Download
            </PrimaryButton>
          </DialogActions>
        </Dialog>

        <FooterContainer>
          <PrimaryButton
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => setDownloadDialogOpen(true)}
            disabled={filteredTransactions.length === 0}
            sx={{
              width: "469px",
              background: "linear-gradient(90deg, #98773b 0%, #ff6b81 100%) !important",
              color: "#fff !important",
              "&:hover": {
                background: "linear-gradient(90deg, #98773b 0%, #ff6b81 100%) !important",
                opacity: 0.9,
              },
            }}
          >
            Download
          </PrimaryButton>
        </FooterContainer>
      </Box>
    </LocalizationProvider>
  );
};

export default TransactionSummaryTab;